import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  BookOpen,
  Compass,
  GraduationCap,
  FileSpreadsheet,
  RotateCcw,
  CheckCircle2,
  Clock,
  ChevronRight,
  AlertCircle,
  CalendarDays,
  RefreshCw,
  Sparkles,
  Target,
  WifiOff,
  PlayCircle,
} from "lucide-react";
import { ApiService } from "../../services/api";
import TeachFlow from "./TeachFlow";
import MakeupRunner from "./MakeupRunner";
import ReadinessCard from "./ReadinessCard";
import WhatsappIzinCard from "./WhatsappIzinCard";
import { coachErrorMessage, useCoachAutoRefresh } from "./coachLive";
import type { CoachTodayResponse, PlanItem, PlanItemYks, YksGerceklikDurum, YksKaynak } from "../../types/navigator";

// Koçun ana ekranı — "bugün ne var". Koç hiçbir işi kendi içinde çözdürmez:
// TEST mevcut E-Eğitim test moduna, TEACH AI öğretmene, MAKEUP telafi setine gider.

const TYPE_META: Record<string, { icon: any; label: string; tone: string }> = {
  TEST: { icon: FileSpreadsheet, label: "Test", tone: "bg-blue-50 text-blue-600" },
  TEACH: { icon: GraduationCap, label: "Konu çalış", tone: "bg-violet-50 text-violet-600" },
  MAKEUP: { icon: RotateCcw, label: "Telafi", tone: "bg-amber-50 text-amber-600" },
  REVIEW: { icon: RotateCcw, label: "Tekrar", tone: "bg-emerald-50 text-emerald-600" },
  VIDEO: { icon: PlayCircle, label: "Konu videosu", tone: "bg-violet-50 text-violet-600" },
  BOOK: { icon: BookOpen, label: "Kitap testi", tone: "bg-teal-50 text-teal-600" },
};

// Yol haritası etiketi — kalem metadata.yks ile geldiyse (Tailwind sınıfları tam yazılır)
const YKS_CHIP: Record<string, { label: string; cls: string }> = {
  ONCELIKLI: { label: "Öncelikli", cls: "border-emerald-200 bg-emerald-50 text-emerald-700" },
  SONRA: { label: "Sonra", cls: "border-amber-200 bg-amber-50 text-amber-700" },
  ATLANABILIR: { label: "Atlanabilir", cls: "border-gray-200 bg-gray-50 text-gray-500" },
};

const KAYNAK_ADI: Record<YksKaynak, string> = {
  deneme: "Son deneme",
  beyan: "Beyanın",
  kanitli: "Testlerinden kanıtlı",
};

const GERCEKLIK_CHIP: Record<YksGerceklikDurum, { label: string; cls: string }> = {
  ustunde: { label: "Hedefin üstündesin", cls: "border-emerald-200 bg-emerald-50 text-emerald-700" },
  yakin: { label: "Yakın", cls: "border-blue-200 bg-blue-50 text-blue-700" },
  orta: { label: "Ulaşılabilir", cls: "border-amber-200 bg-amber-50 text-amber-700" },
  uzak: { label: "İddialı", cls: "border-rose-200 bg-rose-50 text-rose-700" },
  bilinmiyor: { label: "Bilinmiyor", cls: "border-gray-200 bg-gray-50 text-gray-500" },
};

// Verilen ödevin teslim günü ("26 Eki")
const fmtDeadline = (d: string) => new Date(`${d.slice(0, 10)}T12:00:00`).toLocaleDateString("tr-TR", { day: "numeric", month: "short" });
const nfInt = new Intl.NumberFormat("tr-TR", { maximumFractionDigits: 0 });
const nf1 = new Intl.NumberFormat("tr-TR", { maximumFractionDigits: 1 });

function ItemCard({ item, onStart }: { item: PlanItem; onStart: (i: PlanItem) => void }) {
  const meta = (item.action?.kind === "video" ? TYPE_META.VIDEO : TYPE_META[item.type]) || TYPE_META.TEST;
  const Icon = meta.icon;
  const done = item.status === "DONE";
  // Bitmiş konu/eksik kalemine yeniden girilebilir — AI öğretmen kaldığı yerden açılır. Test bir kez
  // çözülebildiği, telafi seti de sonucuyla kapandığı için onlar kapalı kalır.
  const reopenable = done && (item.type === "TEACH" || item.type === "REVIEW");
  const clickable = !done || reopenable;
  const yks = (item.metadata?.yks as PlanItemYks | undefined) || null;
  const yksChip = yks ? YKS_CHIP[yks.etiket] : null;

  return (
    <button
      onClick={() => clickable && onStart(item)}
      disabled={!clickable}
      className={`flex w-full items-center gap-3 rounded-lg border p-4 text-left transition-all ${
        done
          ? `border-gray-100 bg-gray-50/60 ${reopenable ? "hover:border-blue-200" : ""}`
          : "border-gray-200 bg-white hover:border-blue-300 hover:shadow-md"
      }`}
    >
      <div className={`grid h-10 w-10 shrink-0 place-items-center rounded-lg ${done ? "bg-emerald-50 text-emerald-600" : meta.tone}`}>
        {done ? <CheckCircle2 className="h-5 w-5" /> : <Icon className="h-5 w-5" />}
      </div>

      <div className="min-w-0 flex-1">
        <p className={`truncate text-sm font-medium ${done ? "text-gray-400 line-through" : "text-gray-900"}`}>
          {item.title}
        </p>
        <div className="mt-0.5 flex items-center gap-2 text-[11px] text-gray-400">
          <span>{meta.label}</span>
          {item.metadata?.origin === "homework" && (
            <span
              title="Öğretmeninin verdiği ödev — koç teslimine yetişecek güne yerleştirdi"
              className="rounded-full border border-amber-200 bg-amber-50 px-1.5 py-px text-[10px] font-semibold text-amber-700"
            >
              Ödev{item.metadata?.deadline ? ` · teslim ${fmtDeadline(String(item.metadata.deadline))}` : ""}
            </span>
          )}
          {yks && yksChip && (
            <span
              title={`Yol haritası sırası ${yks.sira}`}
              className={`rounded-full border px-1.5 py-px text-[10px] font-medium ${yksChip.cls}`}
            >
              {yksChip.label}
            </span>
          )}
          <span>·</span>
          <span className="inline-flex items-center gap-1">
            <Clock className="h-3 w-3" /> {item.estimatedMinutes} dk
          </span>
          {done && item.result?.percent !== undefined && (
            <>
              <span>·</span>
              <span className="font-medium text-emerald-600">%{item.result.percent}</span>
            </>
          )}
        </div>
      </div>

      {!done && <ChevronRight className="h-5 w-5 shrink-0 text-gray-300" />}
      {reopenable && <span className="shrink-0 text-xs font-medium text-blue-600">Tekrar çalış</span>}
    </button>
  );
}

interface Props {
  onNeedOnboarding: () => void;
}

export default function CoachToday({ onNeedOnboarding }: Props) {
  const navigate = useNavigate();
  const [data, setData] = useState<CoachTodayResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [teach, setTeach] = useState<{ gainId?: string; sectionId?: string } | null>(null);
  const [makeupItemId, setMakeupItemId] = useState<string | null>(null);
  const [makeupSetId, setMakeupSetId] = useState<string | null>(null); // hazırbulunuşluk (plansız set)
  const [readinessKey, setReadinessKey] = useState(0);
  const [revising, setRevising] = useState(false);
  const loadSeq = useRef(0);

  // Hata, liste ekrandayken listeyi SİLMEZ — üstte şerit olarak görünür, bir sonraki başarılı yüklemede kalkar.
  const [tekrarSayisi, setTekrarSayisi] = useState(0);
  useEffect(() => {
    let alive = true;
    ApiService.getBookReviews().then((data) => { if (alive) setTekrarSayisi(data.items.length); }).catch(() => {});
    return () => { alive = false; };
  }, []);

  const load = useCallback(async (silent = false) => {
    const seq = ++loadSeq.current; // üst üste yenilemede geç dönen eski yanıt yenisini ezmesin
    try {
      if (!silent) setLoading(true);
      const res = await ApiService.getCoachToday();
      if (seq !== loadSeq.current) return;
      setData(res);
      setError(null);
      if (!res.hasPlan) onNeedOnboarding();
    } catch (e: any) {
      if (seq === loadSeq.current) setError(coachErrorMessage(e, "Koç yüklenemedi"));
    } finally {
      if (!silent) setLoading(false);
    }
  }, [onNeedOnboarding]);

  useEffect(() => {
    load();
  }, [load]);

  const refresh = useCallback(() => load(true), [load]);
  useCoachAutoRefresh(refresh);

  // Günün işleri öğrencinin GERÇEK oturum süresine göre oturumlara (24 Eki): kısa oturumlu öğrenci bir oturumda
  // bitirebileceğini görür; süresi oturumdan uzun iş kendi oturumunu alır. Veri azsa (ya da tek oturum) düz liste.
  const oturumlar = useMemo(() => {
    if (!data?.oturumDk || !data.items || data.items.length < 2) return null;
    const boy = Math.max(15, data.oturumDk);
    const out: Array<{ items: PlanItem[]; dk: number }> = [];
    for (const it of data.items) {
      const dk = Number(it.estimatedMinutes) || 0;
      const son = out[out.length - 1];
      if (son && son.dk + dk <= boy) { son.items.push(it); son.dk += dk; }
      else out.push({ items: [it], dk });
    }
    return out.length > 1 ? out : null;
  }, [data]);

  // Kalemi başlat — her tür kendi motoruna gider.
  const start = (item: PlanItem) => {
    const a = item.action;
    if (a.kind === "exam" && a.contentId) {
      navigate(`/lessons/play/${a.lessonId}?contentId=${a.contentId}`);
      return;
    }
    if (a.kind === "video" && a.lessonId) {
      navigate(`/lessons/play/${a.lessonId}${a.contentId ? `?contentId=${a.contentId}` : ""}`);
      return;
    }
    if (a.kind === "book") {
      navigate(`/navigator?tab=kitaplar${a.bookId ? `&kitap=${encodeURIComponent(a.bookId)}` : ""}${a.testId ? `&test=${encodeURIComponent(a.testId)}` : ""}`);
      return;
    }
    if (a.kind === "teach") {
      setTeach({ gainId: a.gainId || undefined, sectionId: a.sectionId || undefined });
      return;
    }
    if (a.kind === "makeup") {
      setMakeupItemId(a.itemId);
    }
  };

  const revisePlan = async () => {
    setRevising(true);
    try {
      await ApiService.createCoachPlan();
      await load(true);
    } catch (e: any) {
      setError(coachErrorMessage(e, "Plan yenilenemedi"));
    } finally {
      setRevising(false);
    }
  };

  if (loading) {
    return (
      <div className="grid min-h-[50vh] place-items-center">
        <div className="h-9 w-9 animate-spin rounded-full border-b-2 border-blue-600" />
      </div>
    );
  }

  if (!data) {
    if (!error) return null;
    return (
      <div className="mx-auto max-w-3xl px-4 py-5">
        <div className="flex flex-wrap items-center gap-2 rounded-lg border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700">
          <AlertCircle className="h-5 w-5 shrink-0" />
          <span className="min-w-0 flex-1">{error}</span>
          <button
            onClick={() => load()}
            className="inline-flex shrink-0 items-center gap-1.5 rounded-lg border border-rose-200 bg-white px-3 py-1.5 text-xs font-semibold text-rose-700 transition-colors hover:bg-rose-100"
          >
            <RefreshCw className="h-3.5 w-3.5" /> Tekrar dene
          </button>
        </div>
      </div>
    );
  }

  if (!data.hasPlan) return null;

  const s = data.summary;
  const behind = (s?.missed || 0) >= 3;
  const hedef = data.hedef || null;
  const gerceklik = hedef?.gerceklik ? GERCEKLIK_CHIP[hedef.gerceklik.durum] || GERCEKLIK_CHIP.bilinmiyor : null;

  return (
    <div className="mx-auto w-full max-w-3xl space-y-5 px-4 py-5 sm:px-6">
      {/* Yenileme düştü (çoğunlukla bağlantı) — liste yerinde kalır; internet gelince kendiliğinden tazelenir */}
      {error && (
        <div className="flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5 text-xs text-amber-800">
          <WifiOff className="h-4 w-4 shrink-0" />
          <span className="min-w-0 flex-1">{error}</span>
          <button
            onClick={refresh}
            className="shrink-0 rounded-md border border-amber-200 bg-white px-2.5 py-1 font-semibold transition-colors hover:bg-amber-100"
          >
            Tekrar dene
          </button>
        </div>
      )}

      {/* Koç şeridi */}
      <div className="rounded-lg border border-gray-200 bg-white p-4 sm:p-5">
        <div className="flex items-start gap-3">
          <div className="grid h-11 w-11 shrink-0 place-items-center rounded-lg bg-blue-100 text-blue-600">
            <Compass className="h-6 w-6" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-xs font-medium text-gray-400">Koçun</p>
            <p className="text-base font-semibold leading-snug text-gray-900">{data.coachLine}</p>
          </div>
        </div>

        {/* Geri kaldıysa plan revizyonu öner — gerçek koçun en sık yaptığı iş */}
        {behind && (
          <button
            onClick={revisePlan}
            disabled={revising}
            className="mt-3 inline-flex w-full items-center justify-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-4 py-2.5 text-sm font-semibold text-amber-800 transition-colors hover:bg-amber-100 disabled:opacity-60"
          >
            <RefreshCw className={`h-4 w-4 ${revising ? "animate-spin" : ""}`} />
            {revising ? "Plan yeniden kuruluyor…" : "Planı bana göre yeniden kur"}
          </button>
        )}
      </div>

      {/* Hedef şeridi — yol haritasının özeti: hedef, neredesin, gerçekçi mi */}
      {hedef?.hasHedef && (
        <div className="rounded-lg border border-gray-200 bg-white p-4">
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
            <Target className="h-4 w-4 shrink-0 text-blue-600" />
            <p className="text-sm font-semibold text-gray-900">
              Hedef {hedef.hedefGosterim || (typeof hedef.hedefSiralama === "number" ? nfInt.format(hedef.hedefSiralama) : "—")}
              {typeof hedef.gerekenPuan === "number" && (
                <span className="font-normal text-gray-500"> · gereken {nf1.format(hedef.gerekenPuan)}</span>
              )}
            </p>
            <p className="basis-full text-xs text-gray-500 sm:basis-auto">
              {hedef.mevcut
                ? `${KAYNAK_ADI[hedef.mevcut.kaynak] || hedef.mevcut.kaynak}: ${nf1.format(hedef.mevcut.puan)} ≈ ${hedef.mevcut.yerGosterim || hedef.mevcut.siralamaGosterim}`
                : "durum bilinmiyor"}
            </p>
            {gerceklik && (
              <span className={`ml-auto shrink-0 rounded-full border px-2 py-0.5 text-[10px] font-medium ${gerceklik.cls}`}>
                {gerceklik.label}
              </span>
            )}
          </div>
          {hedef.gerceklik?.mesaj && <p className="mt-1.5 text-[11px] text-gray-500">{hedef.gerceklik.mesaj}</p>}
        </div>
      )}
      {hedef && !hedef.hasHedef && hedef.mevcut && (
        <p className="flex items-start gap-1.5 rounded-lg bg-gray-50 p-2.5 text-xs text-gray-600">
          <Target className="mt-0.5 h-3.5 w-3.5 shrink-0 text-gray-400" />
          <span>
            {hedef.mevcut.kaynak === "deneme" ? "Son denemene" : hedef.mevcut.kaynak === "beyan" ? "Beyanına" : "Testlerine"} göre ≈{" "}
            <b className="text-gray-800">{hedef.mevcut.yerGosterim || hedef.mevcut.siralamaGosterim}</b> bandındasın; hedef girersen yol haritanı çıkarırım.
          </span>
        </p>
      )}

      {/* İlerleme */}
      {s && (
        <div className="flex flex-wrap items-center gap-4 rounded-lg border border-gray-200 bg-white p-4">
          <div className="flex items-center gap-3">
            <div className="grid h-12 w-12 place-items-center rounded-lg bg-blue-50 text-blue-600">
              <span className="text-base font-bold">%{s.percent}</span>
            </div>
            <div>
              <p className="text-xs text-gray-400">Plan ilerlemesi</p>
              <p className="text-sm font-semibold text-gray-900">
                {s.done}/{s.totalItems} tamamlandı
              </p>
            </div>
          </div>
          <div className="ml-auto flex flex-wrap gap-3 text-xs">
            <span className="text-gray-500">
              Bekleyen <b className="text-gray-700">{s.pending}</b>
            </span>
            {s.missed > 0 && (
              <span className="text-amber-600">
                Geciken <b>{s.missed}</b>
              </span>
            )}
            <span className="inline-flex items-center gap-1 text-gray-500">
              <CalendarDays className="h-3.5 w-3.5" /> {s.planEndDate}
            </span>
          </div>
        </div>
      )}

      {/* Plan kurulduktan sonra açılan yeni dersler — plana girmesi için güncelleme öner */}
      {!!data.newLessons?.length && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 sm:p-4">
          <div className="flex items-start gap-2">
            <Sparkles className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
            <p className="min-w-0 flex-1 text-sm text-amber-800">
              <span className="font-semibold">Yeni ders açıldı:</span>{" "}
              {data.newLessons.map((l) => l.title).join(", ")}. Planına eklemek için güncelle.
            </p>
          </div>
          <button
            onClick={revisePlan}
            disabled={revising}
            className="mt-2.5 inline-flex w-full items-center justify-center gap-2 rounded-lg bg-amber-600 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-amber-700 disabled:opacity-60 sm:w-auto"
          >
            <RefreshCw className={`h-4 w-4 ${revising ? "animate-spin" : ""}`} />
            {revising ? "Plan güncelleniyor…" : "Planı güncelle"}
          </button>
        </div>
      )}

      {/* Ölçüm penceresi — hazırbulunuşluk; tüm dersler ölçülünce kaybolur */}
      <ReadinessCard onOpenSet={setMakeupSetId} refreshKey={readinessKey} />

      {/* WhatsApp izni — kurumda açıksa bir kez sorulur, cevap verince kaybolur */}
      <WhatsappIzinCard />

      {/* Bugünün işleri */}
      <div>
        <div className="mb-2 flex items-center gap-2">
          <h2 className="text-base font-semibold text-gray-900">Bugün</h2>
          <span className="text-sm text-gray-400">{data.items.length} iş</span>
        </div>

        {tekrarSayisi > 0 && (
          <button type="button" onClick={() => navigate("/navigator?tab=kitaplar&tekrar=1")} className="mb-2 flex w-full items-center gap-3 rounded-lg border border-amber-200 bg-amber-50 p-3 text-left hover:border-amber-400">
            <RotateCcw className="h-5 w-5 shrink-0 text-amber-600" />
            <span className="min-w-0 flex-1 text-sm font-medium text-gray-900">Bugün {tekrarSayisi} yanlış sorunu tekrar et</span>
            <ChevronRight className="h-4 w-4 shrink-0 text-gray-400" />
          </button>
        )}
        {data.items.length === 0 ? (
          <div className="rounded-lg border border-dashed border-gray-200 bg-gray-50/60 p-8 text-center">
            <CheckCircle2 className="mx-auto mb-2 h-8 w-8 text-emerald-500" />
            <p className="text-sm font-medium text-gray-900">Bugün planında iş yok</p>
            <p className="mt-1 text-xs text-gray-500">Dinlen ya da planından ileriye bakabilirsin.</p>
          </div>
        ) : oturumlar ? (
          <div className="space-y-4">
            <p className="text-xs text-gray-500">İşleri senin gerçek çalışma süren (~{data.oturumDk} dk) kadar oturumlara böldüm; aralarda mola ver.</p>
            {oturumlar.map((o, i) => (
              <div key={i}>
                <p className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-gray-500">{i + 1}. oturum · ~{o.dk} dk</p>
                <div className="space-y-2">
                  {o.items.map((item) => (
                    <ItemCard key={item.id} item={item} onStart={start} />
                  ))}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="space-y-2">
            {data.items.map((item) => (
              <ItemCard key={item.id} item={item} onStart={start} />
            ))}
          </div>
        )}
      </div>

      {teach && (
        <TeachFlow
          gainId={teach.gainId}
          sectionId={teach.sectionId}
          onClose={() => {
            setTeach(null);
            load(true);
          }}
        />
      )}

      {makeupItemId && (
        <MakeupRunner
          itemId={makeupItemId}
          onClose={() => {
            setMakeupItemId(null);
            setReadinessKey((k) => k + 1);
            load(true);
          }}
          onCompleted={() => load(true)}
        />
      )}

      {makeupSetId && (
        <MakeupRunner
          setId={makeupSetId}
          onClose={() => {
            setMakeupSetId(null);
            setReadinessKey((k) => k + 1);
            load(true);
          }}
          onCompleted={() => load(true)}
        />
      )}
    </div>
  );
}
